import { z } from 'zod';

import { zTransitionId } from '../common/identityTransition';

/**
 * Zod schemas for property-related entities
 * These provide runtime validation and type inference
 */

// AffordableHousingProgram schema
const affordableHousingProgramSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  programType: z.enum(['LIHTC', 'HUD', 'State', 'Local'] as const),
  compliancePeriodEnd: z.string(),
  amiPercentage: z.number().nonnegative(),
  setAsideUnits: z.number().int().nonnegative(),
  complianceStatus: z.enum(['Compliant', 'At Risk', 'Non-Compliant'] as const),
});

// LateFeeConfig schema
const lateFeeConfigSchema = z.object({
  enabled: z.boolean(),
  type: z.enum(['fixed', 'percentage'] as const),
  amount: z.number().nonnegative().optional(),
  gracePeriodDays: z.number().int().nonnegative().optional(),
  maxLateFee: z.number().nonnegative().optional(),
});

// Property schema (API/Prisma: coerce type, status, occupancyRate 0-1 to 0-100)
const propertyTypeEnum = ['Single Family', 'Duplex', 'Multifamily', 'Commercial'] as const;
const propertyStatusEnum = ['Stable', 'At Risk', 'Lease-up', 'Renovation', 'Vacant'] as const;
const propertyTypeCoerce = z.string().transform(t => {
  const normalized = (t ?? '').replaceAll('_', ' ');
  return propertyTypeEnum.includes(normalized as (typeof propertyTypeEnum)[number]) ? (normalized as (typeof propertyTypeEnum)[number]) : 'Multifamily';
});
const propertyStatusCoerce = z.string().transform(s => {
  const normalized = (s ?? '').replaceAll('_', ' ');
  return propertyStatusEnum.includes(normalized as (typeof propertyStatusEnum)[number]) ? (normalized as (typeof propertyStatusEnum)[number]) : 'Stable';
});

export const propertySchema = z.object({
  id: zTransitionId,
  name: z.string().transform(n => (n?.trim() || 'Unnamed Property')),
  address: z.string().transform(a => (a?.trim() || 'Address TBD')),
  type: propertyTypeCoerce,
  units: z.number().int().nonnegative(),
  occupancyRate: z.union([z.number(), z.undefined()]).transform(r => (r == null ? 0 : (r >= 0 && r <= 1 ? Math.round(r * 100) : Math.min(100, Math.max(0, r))))),
  image: z.string().nullish(),
  noi: z.number(),
  status: propertyStatusCoerce,
  affordableProgram: affordableHousingProgramSchema.optional(),
  marketRent: z.number().nonnegative().optional(),
  ownerIds: z.array(z.string()).optional(),
  managerId: z.string().nullish(),
  investorIds: z.array(z.string()).optional(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  yearBuilt: z.number().int().positive().optional(),
  bedrooms: z.number().int().nonnegative().optional(),
  bathrooms: z.number().nonnegative().optional(),
  sqft: z.number().nonnegative().optional(),
  lateFeeConfig: lateFeeConfigSchema.optional(),
  photos: z.array(z.string()).optional(),
  featuredPhotoId: z.string().nullish(),
  description: z.string().nullish(),
  amenities: z.array(z.string()).optional(),
  portfolioId: z.string().nullish(),
  isSelfManaged: z.boolean().optional(),
  propertyManagerId: z.string().nullish(),
  managementStartDate: z.string().nullish(),
  managementEndDate: z.string().nullish(),
  repairApprovalThreshold: z.number().int().nonnegative().nullish(),
}).passthrough();

export type Property = z.infer<typeof propertySchema>;

// Unit schema
export const unitSchema = z.object({
  id: zTransitionId,
  propertyId: zTransitionId,
  floorPlanId: z.string(),
  status: z.enum(['Vacant', 'Occupied', 'Renovation'] as const),
  rent: z.number().nonnegative(),
  type: z.enum(['Single Family', 'Duplex', 'Multifamily', 'Commercial'] as const),
  sqft: z.number().nonnegative().optional(),
  bedrooms: z.number().int().nonnegative().optional(),
  bathrooms: z.number().nonnegative().optional(),
  tenantId: z.string().nullable().optional(),
  unitNumber: z.string().optional(),
  balance: z.number().optional(),
});

export type Unit = z.infer<typeof unitSchema>;

// Owner schema (API/Prisma: coerce name, email, status)
const ownerStatusEnum = ['Active', 'Inactive', 'Pending'] as const;

export const ownerSchema = z.object({
  id: z.string(),
  name: z.string().transform(n => (n?.trim() || 'Owner')),
  email: z.string().email().nullish().or(z.literal('')).transform(v => (v == null || v === '' ? 'owner@example.com' : v)),
  phone: z.string().nullish().default(''),
  propertyIds: z.array(z.string()).default([]),
  status: z.string().optional().nullable().transform(v => (v && ownerStatusEnum.includes(v as (typeof ownerStatusEnum)[number]) ? (v as (typeof ownerStatusEnum)[number]) : 'Active')),
  lastLogin: z.string().nullish().default(''),
  totalUnits: z.number().int().nonnegative().nullish().default(0),
  avatar: z.string().nullish().default(''),
  userId: z.string().nullish(),
  financials: z.object({
    totalDistributions: z.number().nonnegative(),
    lastDistribution: z.string(),
    accountBalance: z.number(),
  }).nullish(),
  onboardingCompleted: z.boolean().nullish(),
  companyName: z.string().nullish(),
  taxIdMasked: z.string().nullish(),
  address: z.string().nullish(),
}).passthrough();

export type Owner = z.infer<typeof ownerSchema>;

// Portfolio schema - using passthrough to allow component-specific fields
export const portfolioSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Portfolio name is required').optional(),
  propertyCount: z.number().int().nonnegative().optional(),
  totalUnits: z.number().int().nonnegative().optional(),
  totalValuation: z.number().nonnegative().optional(),
  noi: z.number().optional(),
  occupancy: z.number().min(0).max(100).optional(),
  properties: z.array(z.string()).optional(), // propertyIds
  image: z.string().optional(),
  description: z.string().optional(),
}).passthrough(); // Allow extra fields for component compatibility

export type Portfolio = z.infer<typeof portfolioSchema>;

/** `GET /properties/:propertyId/floorplans` list row. */
export const propertyFloorPlanSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    bed: z.number().nullable(),
    bath: z.number().nullable(),
  })
  .strict();

export type PropertyFloorPlanSummary = z.infer<typeof propertyFloorPlanSummarySchema>;

/** `GET /pm-profiles` published directory row. */
export const pmProfileRowSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    companyName: z.string(),
    dba: z.string().nullable().optional(),
    brokerLicenseNumber: z.string().nullable().optional(),
    licenseState: z.string().nullable().optional(),
    licenseExpiration: z.string().nullable().optional(),
    status: z.string(),
    inviteCode: z.string().nullable().optional(),
    eoPolicyNumber: z.string().nullable().optional(),
    eoCarrier: z.string().nullable().optional(),
    eoExpiry: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    user: z
      .object({
        id: z.string(),
        fullName: z.string(),
        email: z.string(),
      })
      .optional(),
  })
  .passthrough();

export type PmProfileRow = z.infer<typeof pmProfileRowSchema>;
